const EXTENDED_AUDIENCES = ["tripType", "family", "stop", "campus"];

const VALID_CAMPUSES = ["primary", "secondary"];

export const normalizeCampusValue = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const normalized = String(value).trim().toLowerCase();
  return VALID_CAMPUSES.includes(normalized) ? normalized : null;
};

export const formatCampusForApi = (campus) => {
  const normalized = normalizeCampusValue(campus);
  if (normalized === "primary") return "PRIMARY";
  if (normalized === "secondary") return "SECONDARY";
  return null;
};

export const formatCampusDisplayLabel = (campus) => {
  const normalized = normalizeCampusValue(campus);
  if (normalized === "primary") return "Primary";
  if (normalized === "secondary") return "Secondary";
  return campus ? String(campus) : "";
};

export const normalizeCampaignCampusDetails = (campusDetails) => {
  if (!Array.isArray(campusDetails)) return [];
  return campusDetails
    .map((detail) => ({
      campus: detail?.campus ?? null,
    }))
    .filter((detail) => detail.campus);
};

export const normalizeCampaignCampus = (record) => {
  const filter = record?.audienceFilter || {};

  if (Array.isArray(filter.campuses) && filter.campuses.length > 0) {
    return normalizeCampusValue(filter.campuses[0]) || "";
  }

  const campusDetails = normalizeCampaignCampusDetails(record?.campusDetails);
  if (campusDetails.length > 0) {
    return normalizeCampusValue(campusDetails[0].campus) || "";
  }

  if (filter.grade) {
    return normalizeCampusValue(filter.grade) || "";
  }
  if (Array.isArray(filter.grades) && filter.grades.length > 0) {
    return normalizeCampusValue(filter.grades[0]) || "";
  }
  if (record?.campus) {
    return normalizeCampusValue(record.campus) || "";
  }
  if (record?.grade) {
    return normalizeCampusValue(record.grade) || "";
  }
  if (Array.isArray(record?.grades) && record.grades.length > 0) {
    return normalizeCampusValue(record.grades[0]) || "";
  }

  return "";
};

export const normalizeCampaignCampuses = (record) => {
  const filter = record?.audienceFilter || {};
  if (Array.isArray(filter.campuses) && filter.campuses.length > 0) {
    return filter.campuses
      .map((campus) => formatCampusForApi(campus) || String(campus).trim())
      .filter(Boolean)
      .sort();
  }

  const campus = normalizeCampaignCampus(record);
  const apiValue = formatCampusForApi(campus);
  return apiValue ? [apiValue] : [];
};

/** @deprecated Use normalizeCampusValue */
export const normalizeCampaignGradeValue = normalizeCampusValue;

export const normalizeCampaignScheduleDetails = (scheduleDetails) => {
  if (!Array.isArray(scheduleDetails)) return [];
  return scheduleDetails
    .map((schedule) => ({
      id: schedule?.scheduleId ?? schedule?.id ?? null,
      name: schedule?.scheduleName ?? schedule?.name ?? null,
      startTime: schedule?.scheduleStartTime ?? schedule?.startTime ?? null,
    }))
    .sort((a, b) =>
      String(a.id ?? a.name ?? "").localeCompare(
        String(b.id ?? b.name ?? ""),
      ),
    );
};

export const normalizeCampaignFamilyDetails = (familyDetails) => {
  if (!Array.isArray(familyDetails)) return [];
  return familyDetails
    .map((family) => ({
      familyId: family?.familyId ?? family?.id ?? null,
      parentName:
        family?.parentName ??
        family?.parents?.[0]?.parentName ??
        null,
      parents: Array.isArray(family?.parents) ? family.parents : [],
    }))
    .filter((family) => family.familyId)
    .sort((a, b) => String(a.familyId).localeCompare(String(b.familyId)));
};

export const normalizeCampaignStopDetails = (stopDetails) => {
  if (!Array.isArray(stopDetails)) return [];
  return stopDetails
    .map((stop) => ({
      stopId: stop?.stopId ?? stop?.id ?? stop?._id ?? null,
      stopName: stop?.stopName ?? stop?.name ?? null,
    }))
    .filter((stop) => stop.stopId)
    .sort((a, b) => String(a.stopId).localeCompare(String(b.stopId)));
};

export const normalizeCampaignScheduleIds = (record) => {
  if (!record) return [];
  if (Array.isArray(record.scheduleIds) && record.scheduleIds.length > 0) {
    return [...record.scheduleIds].filter(Boolean).sort();
  }
  if (record.scheduleId) {
    return [record.scheduleId];
  }
  return [];
};

export const normalizeCampaignAudienceFilter = (record) => {
  const filter = record?.audienceFilter || {};
  const tripTypeIds = Array.isArray(filter.tripTypeIds)
    ? filter.tripTypeIds
    : record?.tripTypeIds;
  const familyIds = Array.isArray(filter.familyIds)
    ? filter.familyIds
    : record?.familyIds;
  const stopIds = Array.isArray(filter.stopIds)
    ? filter.stopIds
    : record?.stopIds;
  const campuses = normalizeCampaignCampuses(record);
  const campus = normalizeCampaignCampus(record);

  return {
    tripTypeIds: Array.isArray(tripTypeIds)
      ? [...tripTypeIds].filter(Boolean).sort()
      : [],
    tripTypeScope: filter.tripTypeScope || record?.tripTypeScope || "all",
    tripTypeDate: filter.tripTypeDate ?? record?.tripTypeDate ?? null,
    familyIds: Array.isArray(familyIds)
      ? [...familyIds].filter(Boolean).sort()
      : [],
    stopIds: Array.isArray(stopIds) ? [...stopIds].filter(Boolean).sort() : [],
    campuses,
    campus,
  };
};

/** Infer audience type from stored payload (supports legacy single scheduleId campaigns). */
export const inferCampaignTargetAudience = (record) => {
  if (!record) return "all";

  const explicit = record.targetAudience;
  if (explicit && explicit !== "all") return explicit;

  const scheduleIds = normalizeCampaignScheduleIds(record);
  const scheduleDetails = normalizeCampaignScheduleDetails(record.scheduleDetails);
  if (scheduleIds.length > 0 || scheduleDetails.length > 0) {
    return "schedule";
  }

  const filter = normalizeCampaignAudienceFilter(record);
  if (filter.tripTypeIds.length > 0) return "tripType";
  if (filter.familyIds.length > 0) return "family";
  if (filter.stopIds.length > 0) return "stop";
  if (filter.campus) return "campus";

  return explicit || "all";
};

export const buildSparseAudienceFilter = (targetAudience, filter) => {
  switch (targetAudience) {
    case "tripType":
      return {
        tripTypeIds: filter.tripTypeIds,
        tripTypeScope: filter.tripTypeScope || "all",
        tripTypeDate:
          filter.tripTypeScope === "date" ? filter.tripTypeDate || null : null,
      };
    case "family":
      return { familyIds: filter.familyIds };
    case "stop":
      return { stopIds: filter.stopIds };
    case "campus": {
      const campuses = filter.campuses.length > 0
        ? filter.campuses
        : [formatCampusForApi(filter.campus)].filter(Boolean);
      return campuses.length > 0 ? { campuses } : null;
    }
    default:
      return null;
  }
};

/** Flat form fields from API / stored campaign document. */
export const parseCampaignAudienceFromApi = (apiRecord = {}) => {
  const targetAudience = inferCampaignTargetAudience(apiRecord);
  const scheduleIds = normalizeCampaignScheduleIds(apiRecord);
  const filter = normalizeCampaignAudienceFilter(apiRecord);

  return {
    targetAudience,
    scheduleId: scheduleIds[0] || "",
    scheduleIds,
    tripTypeIds: filter.tripTypeIds,
    tripTypeScope: filter.tripTypeScope,
    tripTypeDate: filter.tripTypeDate || "",
    familyIds: filter.familyIds,
    stopIds: filter.stopIds,
    campus: filter.campus,
  };
};

/** Audience fields for create/update API payload. */
export const formatCampaignAudienceForApi = (
  input,
  { extendedEnabled = true } = {},
) => {
  const targetAudience = input.targetAudience || "all";
  const scheduleIds = normalizeCampaignScheduleIds(input);
  const filter = normalizeCampaignAudienceFilter(input);
  const payload = { targetAudience };

  if (targetAudience === "schedule") {
    payload.scheduleIds = scheduleIds;
    if (scheduleIds.length > 0) {
      payload.scheduleId = scheduleIds[0];
    }
  }

  if (extendedEnabled && EXTENDED_AUDIENCES.includes(targetAudience)) {
    const audienceFilter = buildSparseAudienceFilter(targetAudience, filter);
    if (audienceFilter) {
      payload.audienceFilter = audienceFilter;
    }
  }

  return payload;
};

export const serializeCampaignAudience = (record) => {
  if (!record) return "";

  const audience = inferCampaignTargetAudience(record);
  const scheduleDetails = normalizeCampaignScheduleDetails(record.scheduleDetails);
  const scheduleIds = normalizeCampaignScheduleIds(record);

  if (audience === "schedule" || scheduleIds.length > 0 || scheduleDetails.length > 0) {
    if (scheduleDetails.length > 0) {
      return `schedule:${JSON.stringify(scheduleDetails)}`;
    }
    if (scheduleIds.length > 0) {
      return `schedule:${JSON.stringify(scheduleIds)}`;
    }
    return "schedule:[]";
  }

  if (audience === "tripType") {
    const filter = normalizeCampaignAudienceFilter(record);
    return `tripType:${JSON.stringify({
      tripTypeIds: filter.tripTypeIds,
      tripTypeScope: filter.tripTypeScope,
      tripTypeDate: filter.tripTypeDate,
    })}`;
  }

  if (audience === "family") {
    const familyDetails = normalizeCampaignFamilyDetails(record.familyDetails);
    if (familyDetails.length > 0) {
      return `family:${JSON.stringify(familyDetails)}`;
    }
    const filter = normalizeCampaignAudienceFilter(record);
    return `family:${JSON.stringify(filter.familyIds)}`;
  }

  if (audience === "stop") {
    const stopDetails = normalizeCampaignStopDetails(record.stopDetails);
    if (stopDetails.length > 0) {
      return `stop:${JSON.stringify(stopDetails)}`;
    }
    const filter = normalizeCampaignAudienceFilter(record);
    return `stop:${JSON.stringify(filter.stopIds)}`;
  }

  if (audience === "campus") {
    const campusDetails = normalizeCampaignCampusDetails(record.campusDetails);
    if (campusDetails.length > 0) {
      return `campus:${JSON.stringify(campusDetails)}`;
    }
    const filter = normalizeCampaignAudienceFilter(record);
    return `campus:${JSON.stringify(filter.campuses)}`;
  }

  return audience;
};

export const mergeCampaignAudienceRecord = (record, sibling) => {
  const merged = {
    ...(sibling || {}),
    ...(record || {}),
  };
  const scheduleDetails =
    record?.scheduleDetails ?? sibling?.scheduleDetails ?? [];
  const familyDetails =
    record?.familyDetails ?? sibling?.familyDetails ?? [];
  const stopDetails = record?.stopDetails ?? sibling?.stopDetails ?? [];
  const campusDetails =
    record?.campusDetails ?? sibling?.campusDetails ?? [];
  const scheduleIds = normalizeCampaignScheduleIds(merged);
  const audienceFilter = normalizeCampaignAudienceFilter(merged);

  return {
    ...merged,
    targetAudience: inferCampaignTargetAudience(merged),
    scheduleDetails,
    familyDetails,
    stopDetails,
    campusDetails,
    scheduleIds,
    scheduleId: scheduleIds[0] || merged.scheduleId || "",
    audienceFilter: buildSparseAudienceFilter(
      inferCampaignTargetAudience(merged),
      audienceFilter,
    ) || merged.audienceFilter,
    tripTypeIds: audienceFilter.tripTypeIds,
    tripTypeScope: audienceFilter.tripTypeScope,
    tripTypeDate: audienceFilter.tripTypeDate,
    familyIds: audienceFilter.familyIds,
    stopIds: audienceFilter.stopIds,
    campus: audienceFilter.campus,
    campuses: audienceFilter.campuses,
  };
};

const formatTripTypeScopeLabel = (filter) => {
  if (filter.tripTypeScope === "date" && filter.tripTypeDate) {
    return ` on ${filter.tripTypeDate}`;
  }
  return " (upcoming trips)";
};

/** Plain-text summary for audit logs and tables. */
export const formatCampaignAudienceSummary = (record) => {
  const audience = inferCampaignTargetAudience(record);

  if (audience === "all") return "Everyone";

  if (audience === "schedule") {
    const scheduleDetails = record?.scheduleDetails;
    if (Array.isArray(scheduleDetails) && scheduleDetails.length > 0) {
      const names = scheduleDetails
        .map((s) => s?.scheduleName ?? s?.name)
        .filter(Boolean);
      if (names.length > 0) {
        return names.length === 1
          ? names[0]
          : `${names.length} trips: ${names.join(", ")}`;
      }
    }

    const scheduleIds = normalizeCampaignScheduleIds(record);
    if (scheduleIds.length > 0) {
      return scheduleIds.length === 1
        ? scheduleIds[0]
        : `${scheduleIds.length} trips: ${scheduleIds.join(", ")}`;
    }

    if (record?.scheduleName) return record.scheduleName;
    return "Scheduled trips";
  }

  const filter = normalizeCampaignAudienceFilter(record);

  if (audience === "tripType") {
    if (filter.tripTypeIds.length === 0) return "Trip type";
    const ids = filter.tripTypeIds.join(", ");
    return `Trip types: ${ids}${formatTripTypeScopeLabel(filter)}`;
  }

  if (audience === "family") {
    const familyDetails = normalizeCampaignFamilyDetails(record.familyDetails);
    if (familyDetails.length > 0) {
      const names = familyDetails
        .map((family) => family.parentName || family.familyId)
        .filter(Boolean);
      if (names.length > 0) {
        return names.length === 1
          ? names[0]
          : `${names.length} families: ${names.join(", ")}`;
      }
    }

    if (filter.familyIds.length === 0) return "Family";
    const count = filter.familyIds.length;
    return `${count} famil${count === 1 ? "y" : "ies"}: ${filter.familyIds.join(", ")}`;
  }

  if (audience === "stop") {
    const stopDetails = normalizeCampaignStopDetails(record.stopDetails);
    if (stopDetails.length > 0) {
      const names = stopDetails
        .map((stop) => stop.stopName || stop.stopId)
        .filter(Boolean);
      if (names.length > 0) {
        return names.length === 1
          ? names[0]
          : `${names.length} stops: ${names.join(", ")}`;
      }
    }

    if (filter.stopIds.length === 0) return "Stop";
    const count = filter.stopIds.length;
    return `${count} stop${count === 1 ? "" : "s"}: ${filter.stopIds.join(", ")}`;
  }

  if (audience === "campus") {
    const campusDetails = normalizeCampaignCampusDetails(record.campusDetails);
    if (campusDetails.length > 0) {
      const label = formatCampusDisplayLabel(campusDetails[0].campus);
      if (label) return `Campus: ${label}`;
    }

    if (!filter.campus) return "Campus";
    return `Campus: ${formatCampusDisplayLabel(filter.campus)}`;
  }

  return audience;
};
